import React, {
  Fragment,
  useEffect,
  useState,
  useCallback,
  useContext,
} from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useDrag } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import DragItemTypes from "../DragItemTypes";
import { any, concat, includes, propEq, toLower } from "ramda";
import { capitalize } from "../../../util/stringFormat";
import clsx from "clsx";
import { getType } from "../../../util/mediaUtil";
import {
  contentStatus,
  isApproved,
  isApprovedOrPending,
  isRejected,
  useView,
} from "../organizeUtil";
import { useSelected } from "../OrganizeProvider";
import { OrganizeContext } from "../organize-context";
import { useHistory, useLocation } from "react-router-dom";
import { notBlank } from "../../../util/ext";
// import { ContextMenuContext } from "../OrganizeContextMenuProvider";

import Checkbox from "@material-ui/core/Checkbox";
import Grid from "@material-ui/core/Grid";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";

import ContentCard from "../../content/browse/ContentCard";
import ContentOriginCard from "../../common/ContentOriginCard";
import RejectedDialog from "./RejectedDialog";
import RejectionNote from "./RejectionNote";
import StatusIconButton from "./StatusIconButton";
import ContentParentsCard from "../content-groups/card/ContentParentsCard";

const useStyles = makeStyles(theme => ({
  approved: {
    color: theme.palette.approved,
  },

  contentCell: {
    paddingRight: theme.spacing(3),
  },

  contentTypeCell: {
    textTransform: "capitalize",
  },

  disabled: {
    color: `${theme.palette.primary.disabled} !important`,
  },

  dragging: {
    opacity: 0.5,
  },

  icon: {
    color: theme.palette.primary.darker,
  },

  pending: {
    color: theme.palette.primary.brandPrimary,
  },

  root: {
    "&$selected": {
      backgroundColor: theme.palette.primary.main,
      "&> *, &> p": {
        fontWeight: theme.typography.fontWeightMedium,
      },
    },

    "&$selected:hover": {
      backgroundColor: theme.palette.primary.main,
      "&> *, &> p": {
        fontWeight: theme.typography.fontWeightMedium,
      },
    },
  },

  rejected: {
    color: theme.palette.rejected,
  },

  rejectedTooltip: {
    boxShadow: "0px 0px 20px rgba(0, 0, 0, 0.15)",
    backgroundColor: "white",
    color: "black",
  },

  reviewTitle: {
    paddingLeft: theme.spacing(6),
  },

  selected: {},
}));

const DraggableRow = props => {
  const classes = useStyles();
  const {
    currentStatus,
    loading,
    content,
    handleOpenViewerClick,
    handlePendingClick,
    searchState,
    sourceFilter,
  } = props;

  const view = useView();
  const history = useHistory();
  const location = useLocation();

  const {
    removeAllSelectedContent,
    selectedContent,
    updateActiveSelectedMediaType,
    updateSelectedContent,
  } = useSelected();

  const handleRowClick = content => event => {
    if (
      includes(toLower(currentStatus), [
        contentStatus.approved,
        contentStatus.pending,
      ])
    ) {
      updateSelectedContent(content);
      updateActiveSelectedMediaType("content");
    }
  };

  const changeStatus = newStatus => async event => {
    await handlePendingClick(content.id, newStatus);
    removeAllSelectedContent();
  };

  const [, drag, preview] = useDrag(
    () => ({
      type: DragItemTypes.CONTENT,
      item: {
        id: content.id,
        mediaType: "content",
      },
      canDrag: () => isApproved(view),
      isDragging: () => isApproved(view),
      collect: monitor => ({
        isDragging: monitor.isDragging(),
        canDrag: monitor.canDrag(),
      }),
    }),
    [content, view],
  );

  const context = useContext(OrganizeContext);

  const handleDragStart = () => {
    if (!any(propEq("id", content.id), selectedContent)) {
      updateSelectedContent({ id: content.id });
    }
    if (isApproved(view)) {
      context.updateIsDragging(true);
    }
  };

  const handleDragEnd = () => {
    context.updateIsDragging(false);
  };

  // This is required for creating a custom drag overlay
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  // const { handleContextMenuClick } = useContext(ContextMenuContext);

  const handleViewerClick = event => {
    event.stopPropagation();
    handleOpenViewerClick(content);
  };

  const handleContentEditClick = useCallback(
    event => {
      event.stopPropagation();
      const { pathname, search } = location;
      const fromPath = notBlank(search) ? pathname + search : pathname;
      history.push(`/creator/content/${content.id}`, {
        from: fromPath,
        searchState,
      });
    },
    [content.id, history, location, searchState],
  );

  //-- Manage Rejected Dialog
  const [open, setOpen] = useState(false);
  const handleRejectedClick = event => {
    event.stopPropagation();
    setOpen(true);
  };
  const handleDialogClose = () => setOpen(false);
  const handleReject = async () => {
    await handlePendingClick(content.id, "REJECTED");
    removeAllSelectedContent();
  };

  return (
    <Fragment>
      <RejectedDialog
        open={open}
        content={content}
        parents={concat(content.parentModules, content.parentCollections)}
        handleClose={handleDialogClose}
        handleReject={handleReject}
      />
      <TableRow
        ref={drag}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        id={content.id}
        classes={{
          root: clsx(classes.root, {
            [classes.dragging]: context.state.isDragging,
          }),
        }}
        onClick={handleRowClick(content)}
        hover
        tabIndex={-1}
        role="checkbox"
        aria-checked={any(propEq("id", content.id), selectedContent)}
        selected={any(propEq("id", content.id), selectedContent)}
      >
        {isApprovedOrPending(view) && (
          <TableCell padding="checkbox" align="center">
            <Checkbox
              color="primary"
              className={clsx(classes.checkbox, {
                [classes.selectedCheckbox]: any(
                  propEq("id", content.id),
                  selectedContent,
                ),
              })}
              checked={any(propEq("id", content.id), selectedContent)}
              inputProps={{
                "aria-labelledby": "content-checkbox-" + content.id,
              }}
            />
          </TableCell>
        )}
        <TableCell
          key={"title:" + content.id}
          align="left"
          className={classes.contentCell}
          classes={{
            root: classes.reviewTitle,
          }}
        >
          <ContentCard
            disabled={any(propEq("id", content.id), selectedContent)}
            loading={loading}
            handleThumbnailClick={
              any(propEq("id", content.id), selectedContent)
                ? null
                : handleViewerClick
            }
            handleTitleClick={
              any(propEq("id", content.id), selectedContent)
                ? null
                : handleContentEditClick
            }
            content={content}
            selected={any(propEq("id", content.id), selectedContent)}
          />
        </TableCell>

        {isRejected(view) && (
          <TableCell key={`rejected-note-${content.id}`} align="left">
            <RejectionNote content={content} />
          </TableCell>
        )}

        {isApproved(view) ? (
          <TableCell key={"date-updated:" + content.id} align="left">
            {new Date(content.auditUpdated).toLocaleDateString()}
          </TableCell>
        ) : (
          <TableCell key={"date-added:" + content.id} align="left">
            {new Date(content.auditAdded).toLocaleDateString()}
          </TableCell>
        )}

        <TableCell
          key={"content-type:" + content.id}
          align="left"
          classes={{
            root: classes.contentTypeCell,
          }}
        >
          {capitalize(getType(content))}
        </TableCell>

        <TableCell key={"origin:" + content.id} align="left">
          <ContentOriginCard content={content} sourceFilter={sourceFilter} />
        </TableCell>

        {isApproved(view) && (
          <TableCell
            onClick={e => e.stopPropagation()}
            key={"included-in:" + content.id}
            align="left"
          >
            <ContentParentsCard content={content} />
          </TableCell>
        )}

        <TableCell key={`action-${content.id}`}>
          <Grid
            container
            justify="space-between"
            alignItems="center"
            style={{ width: 80 }}
          >
            {!isApproved(view) && (
              <StatusIconButton
                disabled={notBlank(selectedContent)}
                status="approved"
                onClick={changeStatus("APPROVED")}
              />
            )}

            {isRejected(view) && (
              <StatusIconButton
                disabled={notBlank(selectedContent)}
                status="pending"
                onClick={changeStatus("PENDING")}
              />
            )}

            {isApprovedOrPending(view) && (
              <StatusIconButton
                disabled={notBlank(selectedContent)}
                status="rejected"
                onClick={handleRejectedClick}
              />
            )}
          </Grid>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};

export default DraggableRow;
